/**
 * ZoneMapper — Extracted from TileMeta.
 *
 * Builds the zone-to-tile-ID mapping that generators consume.
 * This is the bridge between abstract zones (ZONE.GRASS, ZONE.KITCHEN)
 * and actual tile IDs from a specific tileset.
 */

import { ZONE } from './ZoneTypes.js';

export class ZoneMapper {
  /**
   * @param {import('./TileMeta.js').TileMeta} tileMeta
   */
  constructor(tileMeta) {
    this._meta = tileMeta;
  }

  /**
   * Build a zone-to-tile mapping.
   * @param {Object} zoneConfig — Optional overrides: { [zoneId]: terrainId/floorId }
   * @returns {Object<number, number>} — { zoneId: tileId }
   */
  build(zoneConfig = {}) {
    const mapping = {};

    this._mapTerrainZones(mapping, zoneConfig);
    this._mapFloorZones(mapping, zoneConfig);
    this._mapWallZones(mapping, zoneConfig);
    this._mapOpenings(mapping, zoneConfig);

    return mapping;
  }

  _mapTerrainZones(mapping, config) {
    const terrainZones = {
      [ZONE.GRASS]: 'grass', [ZONE.DIRT]: 'dirt', [ZONE.WATER]: 'water',
      [ZONE.STONE]: 'stone', [ZONE.PATH]: 'path', [ZONE.SAND]: 'sand',
      [ZONE.COBBLE]: 'cobble', [ZONE.ROAD]: 'road', [ZONE.LAVA]: 'lava',
      [ZONE.ICE]: 'ice', [ZONE.ASPHALT]: 'asphalt', [ZONE.SIDEWALK]: 'sidewalk',
      [ZONE.DRIVEWAY]: 'driveway', [ZONE.PARKING]: 'parking',
    };

    for (const [zoneId, defaultTerrainId] of Object.entries(terrainZones)) {
      const terrainId = config[zoneId] || defaultTerrainId;
      const tg = this._meta.getTerrain(terrainId);
      if (tg) mapping[zoneId] = tg.centerTile;
    }
  }

  _mapFloorZones(mapping, config) {
    const floorZones = {
      [ZONE.FLOOR]: 'FLOOR', [ZONE.BEDROOM]: 'BEDROOM', [ZONE.BATHROOM]: 'BATHROOM',
      [ZONE.KITCHEN]: 'KITCHEN', [ZONE.LIVING]: 'LIVING', [ZONE.DINING]: 'DINING',
      [ZONE.OFFICE]: 'OFFICE', [ZONE.MASTER]: 'MASTER', [ZONE.STORE]: 'STORE',
      [ZONE.STORAGE]: 'STORAGE', [ZONE.HALLWAY]: 'HALLWAY', [ZONE.LAUNDRY]: 'LAUNDRY',
      [ZONE.BACKROOM]: 'BACKROOM', [ZONE.RESTROOM]: 'RESTROOM',
    };

    for (const [zoneId, zoneName] of Object.entries(floorZones)) {
      const floorId = config[zoneId] || null;
      if (floorId) {
        const ft = this._meta.getFloorType(floorId);
        if (ft) { mapping[zoneId] = ft.tile; continue; }
      }
      const candidates = this._meta.getFloorsForZone(zoneName);
      if (candidates.length > 0) mapping[zoneId] = candidates[0].tile;
    }
  }

  _mapWallZones(mapping, config) {
    const wallZones = {
      [ZONE.WALL]: 'top', [ZONE.WALL_EXT]: 'top', [ZONE.WALL_INT]: 'top',
      [ZONE.WALL_TOP]: 'top', [ZONE.WALL_H]: 'left', [ZONE.WALL_V]: 'top',
    };

    let wallSetId = config.wallSet || null;
    if (!wallSetId) {
      const ids = this._meta.getWallSetIds();
      if (ids.length > 0) wallSetId = ids[0];
    }

    if (wallSetId) {
      for (const [zoneId, pos] of Object.entries(wallZones)) {
        mapping[zoneId] = this._meta.resolveWall(wallSetId, pos);
      }
    }
  }

  _mapOpenings(mapping, config) {
    const doorOpenings = this._meta.getOpeningsByType('door');
    const windowOpenings = this._meta.getOpeningsByType('window');
    const doorOpening = config.door ? this._meta.getOpening(config.door) : doorOpenings[0];
    const windowOpening = config.window ? this._meta.getOpening(config.window) : windowOpenings[0];

    if (doorOpening) {
      mapping[ZONE.DOOR] = doorOpening.tile;
      mapping[ZONE.DOORFRAME] = doorOpening.tileOpen ?? doorOpening.tile;
    }
    if (windowOpening) {
      mapping[ZONE.WINDOW] = windowOpening.tile;
    }
  }
}
